import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, MapPin, PenLine } from 'lucide-react';
import { Loader2, ArrowLeft, Undo2, XCircle, Send, CheckCircle2, CalendarClock, Download, History, X, Repeat, MessageSquareText, FileSearch, BellRing, QrCode, Link2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { PassagesTracker } from './PassagesTracker';
import { VisitesMission } from './VisitesMission';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import {
    PIPELINE_STAGE_LABELS_PRESTATION, PipelineStagePrestation,
    isTerminalStagePrestation, buildDisplayStagesPrestation,
} from './pipelineStagesPrestation';
import { DevisPrestationComposer } from './DevisPrestationComposer';
import { DevisSigneApercuPrestation } from './DevisSigneApercuPrestation';
import { DocumentsDuDossier } from './ApercuDocument';
import { MenuPlus } from './MenuPlus';
import { echeancier } from '../../lib/echeancier';
import { ContratApercuInline } from './ContratApercuInline';
import { ContratSigneApercuPrestation } from './ContratSigneApercuPrestation';
import { FormulaPassagesPlanner, DevisItemDraft } from './FormulaPassagesPlanner';
import { OrigineDemandeDevis, OrigineDemande } from './OrigineDemandeDevis';
import { DemarchageDomicile } from './DemarchageDomicile';
import { SignatureSurPlaceModal } from './SignatureSurPlaceModal';
import { useConfirm } from '../ui/ConfirmProvider';
import { SuiviDossierResume } from './SuiviDossierResume';

const HISTORY_ACTION_LABELS: Record<string, string> = {
    cree: 'Devis créé', envoye: 'Devis envoyé', renvoye: 'Devis renvoyé (même mail)', relance: 'Relance envoyée',
    signe_recu: 'Signature reçue du client', signe_valide: 'Signature validée par l’établissement', refuse: 'Devis refusé par le client',
};

// Même petite modale « Historique » que le module Devis autonome — dupliquée volontairement (composant
// minuscule, éviter un import croisé pour si peu).
const fmtDateHeurePipeline = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
};

// Résumé d'une signature en ligne (lot S1) : qui a signé, quand, depuis quelle adresse IP, ce qui a été
// accepté — et le fichier de preuve à conserver en cas de contestation. C'est ce dossier de preuve, pas
// l'image d'une signature manuscrite, qui rend la signature opposable (eIDAS art. 25, niveau simple).
// L'établissement ne voit que qui a signé et quand (lot S1-bis, Emeni 2026-09-22) : l'IP, le navigateur,
// les empreintes et le fichier de preuve sont conservés par L'IAmani et consultables par le super admin.
function SignatureEnLigneResume({ signature }: { signature: any }) {
    return (
        <div className="text-xs text-content-muted space-y-0.5">
            <p>
                Signé en ligne par <span className="font-bold text-content">{signature.signer_name}</span>
                {signature.signed_at ? ` le ${fmtDateHeurePipeline(signature.signed_at)}` : ''}, email vérifié par code à usage unique.
            </p>
            <p>
                {signature.cgv_accepted ? 'CGV acceptées. ' : ''}
                {signature.retractation_days > 0
                    ? `Délai de rétractation : ${signature.retractation_days} jours${signature.retractation_waiver ? ' (exécution anticipée demandée par le client)' : ''}.`
                    : 'Aucun délai de rétractation applicable.'}
            </p>
        </div>
    );
}

// Moyen de paiement choisi par le client après la signature du devis (enregistré seulement : aucun prélèvement n'est déclenché).
const LIBELLE_QUAND_PAIEMENT: Record<string, string> = {
    lien_1x: 'en une fois', lien_3x: 'en trois fois', apres_prestation: 'après chaque prestation', une_fois: 'en une seule fois', mensuel: 'mensualisé', mensuel_12: 'mensualisé sur 12 mois', mensuel_24: 'mensualisé sur 24 mois', fin_contrat: 'en fin de contrat',
};
const LIBELLE_MOYEN_PAIEMENT: Record<string, string> = { lien: 'Lien de paiement', carte: 'Carte bancaire', sepa: 'Prélèvement SEPA' };
function PaiementChoisi({ dossier }: { dossier: any }) {
    if (!(dossier.devis_signed_received_at || dossier.devis_signed_at)) return null;
    // Un particulier n'a aucun choix de paiement (lien Stripe en une fois) : rien à afficher, sauf un ancien choix déjà enregistré.
    if (dossier.type !== 'PROFESSIONNEL' && !dossier.payment_choice) return null;
    const moyen = LIBELLE_MOYEN_PAIEMENT[dossier.payment_choice_method] || '';
    return (
        <div className="rounded-xl border border-border bg-surface-soft px-3.5 py-2.5 text-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Paiement choisi par le client</p>
            {dossier.payment_choice ? (
                <p className="font-bold text-content mt-0.5">{moyen}{moyen ? ', ' : ''}{LIBELLE_QUAND_PAIEMENT[dossier.payment_choice] || dossier.payment_choice}
                    {dossier.payment_choice_at && <span className="font-normal text-xs text-content-muted"> · le {fmtDateHeurePipeline(dossier.payment_choice_at)}</span>}</p>
            ) : (
                <p className="text-xs text-content-muted mt-0.5">Pas encore choisi : le client le choisit sur la page de son devis signé.</p>
            )}
            {dossier.payment_commitment_at && (
                <p className="text-xs text-emerald-600 mt-1">Engagement de payer jusqu'au bout accepté le {fmtDateHeurePipeline(dossier.payment_commitment_at)}.</p>
            )}
        </div>
    );
}

function HistoriqueDevisModal({ devisId, onClose }: { devisId: string; onClose: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [rows, setRows] = React.useState<any[]>([]);
    const [sentTo, setSentTo] = React.useState<string | null>(null);
    const [sentAt, setSentAt] = React.useState<string | null>(null);
    const [sentSubject, setSentSubject] = React.useState<string | null>(null);
    const [sentHtml, setSentHtml] = React.useState<string | null>(null);
    const [apercuOuvert, setApercuOuvert] = React.useState(false);
    React.useEffect(() => {
        moduleApi.getPrestationDevisHistory(devisId).then((r: any) => {
            setRows(Array.isArray(r?.rows) ? r.rows : []);
            setSentTo(r?.sent_to || null); setSentAt(r?.sent_at || null);
            setSentSubject(r?.sent_subject || null); setSentHtml(r?.sent_html || null);
        }).finally(() => setLoading(false));
    }, [devisId]);
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-border rounded-2xl p-5 w-full max-w-md space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <p className="text-base font-black text-content">Historique du devis</p>
                    <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                </div>
                {sentTo && (
                    <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                        <p className="text-xs text-content-muted">Envoyé à <span className="font-bold text-content">{sentTo}</span>{sentAt && <span> — le <span className="font-bold text-content">{fmtDateHeurePipeline(sentAt)}</span></span>}</p>
                        {sentHtml && (
                            <button onClick={() => setApercuOuvert((v) => !v)} className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                {apercuOuvert ? "Masquer l'aperçu du mail" : "Voir l'aperçu du mail reçu"}
                            </button>
                        )}
                        {apercuOuvert && sentHtml && (
                            <div className="rounded-lg border border-border overflow-hidden bg-white" style={{ height: '50vh' }}>
                                {sentSubject && <p className="text-[11px] font-bold text-slate-700 bg-slate-50 px-2 py-1.5 border-b border-slate-200">{sentSubject}</p>}
                                <iframe srcDoc={sentHtml} title="Aperçu du mail" className="w-full h-full border-0" />
                            </div>
                        )}
                    </div>
                )}
                {loading ? (
                    <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-content-muted" /></div>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-content-muted py-4 text-center">Aucun événement pour ce devis.</p>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((r) => (
                            <li key={r.id} className="py-2.5">
                                <p className="text-sm font-bold text-content">{HISTORY_ACTION_LABELS[r.action] || r.action}</p>
                                {r.note && <p className="text-xs text-content-muted mt-0.5">{r.note}</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

const fmtMontant = (n?: number | null) =>
    typeof n === 'number' ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n) : '—';
const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

/**
 * Stepper plein écran du dossier de Suivi de prestation — même esprit que DossierPipelinePage.tsx côté
 * Suivi privatisation (frise + panneau d'action de l'étape courante + historique), mais code entièrement
 * propre à ce module, et un panneau d'action bien plus simple puisque le pipeline n'a que 6 étapes.
 *
 * Le serveur reste seul juge de ce qui est permis (POST /prestation-dossiers/:id/transition) : cet écran
 * n'affiche que ce que la route accepterait, mais ne remplace jamais son contrôle.
 */
export function DossierPrestationPipelinePage() {
    const { dossierId } = useParams<{ dossierId: string }>();
    const navigate = useNavigate();
    const confirmAction = useConfirm();
    const { user } = useAuth();
    const companyName = (user as any)?.company_name || (user as any)?.companyName || (user as any)?.name || '';
    const logoUrl = (user as any)?.logoUrl || (user as any)?.logo_url || null;
    const [dossier, setDossier] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [composeurOuvert, setComposeurOuvert] = React.useState(false);
    // Renseigné par FormulaPassagesPlanner quand le client a une formule (agrège les passages en lignes de
    // devis) — null quand il n'y a pas de formule, le composeur part alors vide comme avant.
    const [planPasses, setPlanPasses] = React.useState<{ items: DevisItemDraft[]; formulaSubscriptionId: string } | null>(null);
    // Un devis en brouillon déjà composé pour ce dossier (auto-composition en arrière-plan du composeur,
    // ou reprise après avoir quitté la page) doit rouvrir DIRECTEMENT le composeur dessus — jamais repartir
    // du planning des passages vierge, qui ferait perdre ce qui était déjà saisi (signalé par Emeni,
    // 2026-09-03 : le brouillon existait bien dans le module Devis mais le dossier l'ignorait).
    const [editionDevisId, setEditionDevisId] = React.useState<string | null>(null);
    const [historiqueDevisId, setHistoriqueDevisId] = React.useState<string | null>(null);

    // Demande venue du simulateur du site : sa selection, son estimation, ses photos. Chargee ici (et pas
    // dans le composant d'affichage) parce qu'elle sert aussi a preremplir le devis en un clic.
    const [origine, setOrigine] = React.useState<OrigineDemande | null>(null);
    const [itemsDemande, setItemsDemande] = React.useState<DevisItemDraft[] | undefined>(undefined);

    const [candidats, setCandidats] = React.useState<any[]>([]);
    const [candidatChoisi, setCandidatChoisi] = React.useState('');

    const [abandonOuvert, setAbandonOuvert] = React.useState(false);
    const [motifAbandon, setMotifAbandon] = React.useState('');

    // Contrat d'abonnement (module Contrats d'abonnement) — ne se crée QUE depuis ici, une fois le devis
    // signé : c'est le seul moment où la formule souscrite et son prix réel sont connus. L'écran Abonnements
    // reste un pur suivi, jamais un point de création (demande d'Emeni, 2026-09-03).
    const [templates, setTemplates] = React.useState<any[]>([]);
    const [contratOuvert, setContratOuvert] = React.useState(false);
    const [templateId, setTemplateId] = React.useState('');
    // « Mon contrat » allumé pour ce type de client : un seul contrat, plus de choix de modèle (Contrats annuels › Réglages).
    const [monContratActif, setMonContratActif] = React.useState(false);
    const [engagementMonths, setEngagementMonths] = React.useState(12);
    const [prixMensuel, setPrixMensuel] = React.useState('');
    const [paymentMode, setPaymentMode] = React.useState<'mensuel' | 'a_intervention'>('mensuel');
    const [contratBusy, setContratBusy] = React.useState(false);
    const [contratErreur, setContratErreur] = React.useState('');
    // Le module « Contrats d'abonnement » est-il disponible ? Connu à la réponse de la liste des modèles.
    const [contratDispo, setContratDispo] = React.useState(false);
    const prixMensuelModifie = React.useRef(false);
    const depotContratRef = React.useRef<HTMLInputElement>(null);
    // Contrôle du contrat AVANT envoi : ce qui manquerait au document que le client va signer (identité du
    // prestataire, adresse du client) ou qui le rendrait incohérent (moins de passages chiffrés que la formule
    // n'en comprend). Calculé sur les mêmes données que le PDF — rien n'est bloquant, tout est signalé.
    const [contratAlertes, setContratAlertes] = React.useState<string[]>([]);
    React.useEffect(() => {
        const subId = dossier?.subscription?.id;
        const statut = dossier?.subscription?.status;
        if (!subId || !['brouillon', 'envoye', 'consulte'].includes(statut)) { setContratAlertes([]); return; }
        let annule = false;
        moduleApi.getSubscriptionContractData(subId).then((d: any) => {
            if (annule || !d || d.error) return;
            const a: string[] = [];
            const pr = d.prestataire || {};
            const manque = [!pr.company_name && "nom de l'entreprise", !pr.siret && 'SIRET', !(pr.address && pr.city) && 'adresse'].filter(Boolean);
            if (manque.length) a.push(`Vos informations légales sont incomplètes (${manque.join(', ')}) : complétez-les dans Paramètres avant d'envoyer le contrat.`);
            const ct = d.contact || {};
            if (!(ct.address && ct.city)) a.push("L'adresse du client n'est pas renseignée sur sa fiche : elle figurera vide dans le contrat.");
            const nbPassages = new Set((d.prestations || []).map((x: any) => x.passage_number ?? 'u')).size;
            const attendus = Number(d.subscription?.occurrences_total) || 0;
            if (!Number(d.subscription?.passages_libres) && attendus > 1 && nbPassages < attendus) {
                a.push(`Le devis ne chiffre que ${nbPassages} passage${nbPassages > 1 ? 's' : ''} sur les ${attendus} inclus dans la formule : le contrat annoncera ${attendus} interventions pour le prix de ${nbPassages}.`);
            }
            if (d.eligibility_met === false) a.push("Le seuil de la formule n'est pas atteint sur au moins un passage.");
            setContratAlertes(a);
        }).catch(() => {});
        return () => { annule = true; };
    }, [dossier?.subscription?.id, dossier?.subscription?.status]);

    const charger = React.useCallback(() => {
        if (!dossierId) return;
        setLoading(true);
        moduleApi.getPrestationDossier(dossierId)
            .then((r: any) => { if (r?.error) setErreur(r.error); else setDossier(r); })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, [dossierId]);

    React.useEffect(() => { charger(); }, [charger]);

    // Reprend automatiquement un devis déjà en brouillon pour ce dossier (composé plus tôt, y compris par
    // l'auto-composition en arrière-plan du composeur) — sans ça, revenir sur le dossier après l'avoir
    // quitté repartait du planning des passages vierge alors que le brouillon existait déjà.
    React.useEffect(() => {
        if (dossier?.stage === 'dossier_cree' && dossier?.devis?.id && dossier.devis.status === 'brouillon' && !composeurOuvert) {
            setEditionDevisId(dossier.devis.id);
            setComposeurOuvert(true);
        }
    }, [dossier?.stage, dossier?.devis?.id, dossier?.devis?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (dossier?.source !== 'simulateur' || !dossierId) { setOrigine(null); return; }
        moduleApi.getPrestationDossierOrigine(dossierId)
            .then((r: any) => { if (r && !r.error) setOrigine(r); })
            .catch(() => {});
    }, [dossier?.source, dossierId]);

    React.useEffect(() => {
        if (dossier?.stage === 'devis_signe' && dossierId) {
            moduleApi.getPrestationDossierCandidateAppointments(dossierId).then((r: any) => setCandidats(Array.isArray(r) ? r : [])).catch(() => setCandidats([]));
        }
    }, [dossier?.stage, dossierId]);

    React.useEffect(() => {
        if ((dossier?.devis?.formula_subscription_id || dossier?.devis?.formula_id) && !dossier.subscription) {
            moduleApi.getContractTemplates().then((r: any) => {
                if (!Array.isArray(r)) { setContratDispo(false); setTemplates([]); return; }
                const actifs = r.filter((t: any) => t.is_active);
                setContratDispo(true); setTemplates(actifs);
                // Un seul modèle : inutile de faire choisir.
                if (actifs.length === 1) setTemplateId((cur) => cur || actifs[0].id);
                moduleApi.getContratClauses().then((c: any) => {
                    const actif = !!c?.profils?.[dossier?.type === 'PROFESSIONNEL' ? 'pro' : 'part']?.choix?.moteur_actif;
                    setMonContratActif(actif);
                    // Le modèle ne change plus rien au texte : on prend le premier pour la création, sans le demander.
                    if (actif && actifs.length > 0) setTemplateId((cur) => cur || actifs[0].id);
                }).catch(() => setMonContratActif(false));
            }).catch(() => { setContratDispo(false); setTemplates([]); });
        }
    }, [dossier?.devis?.formula_subscription_id, dossier?.devis?.formula_id, dossier?.subscription]);

    // Prix mensuel = total annuel du devis / nombre de mois : recalculé quand on change la durée, tant que
    // l'établissement n'a pas saisi son propre montant (2026-09-03 : le champ affichait le total annuel).
    React.useEffect(() => {
        if (prixMensuelModifie.current || !dossier?.devis?.total_ttc) return;
        setPrixMensuel((Math.round(Number(dossier.devis.total_ttc) / Math.max(1, engagementMonths) * 100) / 100).toFixed(2));
    }, [dossier?.devis?.total_ttc, engagementMonths]);

    // Envoi, aperçu, dépôt manuel et validation du contrat — repris directement ici pour ne jamais devoir
    // quitter le dossier vers l'écran Abonnements (demande d'Emeni, 2026-09-04), même logique et mêmes
    // appels que SubscriptionsModule.tsx.
    const [contratSendBusy, setContratSendBusy] = React.useState(false);
    const [contratPreviewBusy, setContratPreviewBusy] = React.useState(false);
    const [contratDepotBusy, setContratDepotBusy] = React.useState(false);
    const [contratValiderBusy, setContratValiderBusy] = React.useState(false);
    const [contratSupprBusy, setContratSupprBusy] = React.useState(false);

    const apercuContratInline = async () => {
        if (!dossier?.subscription?.id) return;
        setContratPreviewBusy(true); setContratErreur('');
        try {
            const data: any = await moduleApi.getSubscriptionContractData(dossier.subscription.id);
            if (data?.error) throw new Error(data.error);
            const { generateContractPdfFromData } = await import('../Abonnements/contractPdfGenerator');
            const { blob } = await generateContractPdfFromData(data);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (e: any) {
            setContratErreur(e?.message || "Erreur lors de la préparation de l'aperçu");
        } finally {
            setContratPreviewBusy(false);
        }
    };

    const envoyerContratInline = async () => {
        if (!dossier?.subscription?.id) return;
        setContratSendBusy(true); setContratErreur('');
        try {
            const data: any = await moduleApi.getSubscriptionContractData(dossier.subscription.id);
            if (data?.error) throw new Error(data.error);
            const { generateContractPdfBase64 } = await import('../Abonnements/contractPdfGenerator');
            const document_base64 = await generateContractPdfBase64(data);
            const res: any = await moduleApi.sendSubscription(dossier.subscription.id, { document_base64 });
            if (res?.error) { setContratErreur(res.error); return; }
            charger();
        } catch (e: any) {
            setContratErreur(e?.message || "Erreur lors de l'envoi du contrat");
        } finally {
            setContratSendBusy(false);
        }
    };

    const deposerContratSigneInline = async (file: File) => {
        if (!dossier?.subscription?.id || contratDepotBusy) return;
        setContratDepotBusy(true); setContratErreur('');
        try {
            const document_base64: string = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(file);
            });
            const res: any = await moduleApi.deposerContratSigneManuel(dossier.subscription.id, { document_base64, file_name: file.name, mime_type: file.type || 'application/pdf' });
            if (res?.error) { setContratErreur(res.error); return; }
            charger();
        } catch (e: any) {
            setContratErreur(e?.message || 'Erreur lors du dépôt du contrat signé.');
        } finally {
            setContratDepotBusy(false);
        }
    };

    const validerContratInline = async () => {
        if (!dossier?.subscription?.id) return;
        setContratValiderBusy(true); setContratErreur('');
        try {
            const res: any = await moduleApi.validerSubscription(dossier.subscription.id);
            if (res?.error) { setContratErreur(res.error); return; }
            charger();
        } catch (e: any) {
            setContratErreur(e?.message || 'Erreur lors de la validation');
        } finally {
            setContratValiderBusy(false);
        }
    };

    // Retour arrière sur un contrat créé par erreur (2026-09-06, Emeni : "je me suis trompé et je ne
    // pouvais pas revenir en arrière") — possible uniquement tant qu'il est en brouillon, donc jamais
    // envoyé ni signé (même garde-fou que DELETE /subscriptions/:id côté serveur). Le formulaire de
    // création réapparaît juste après, prêt à être resaisi correctement.
    const supprimerContratBrouillon = async () => {
        if (!dossier?.subscription) return;
        if (!(await confirmAction({ title: 'Supprimer le contrat en brouillon ?', description: <>Vous pourrez le recréer avec les bons paramètres.</>, confirmLabel: 'Supprimer le brouillon' }))) return;
        setContratSupprBusy(true); setContratErreur('');
        try {
            const res: any = await moduleApi.deleteSubscription(dossier.subscription.id);
            if (res?.error) { setContratErreur(res.error); return; }
            setContratOuvert(false);
            charger();
        } catch (e: any) {
            setContratErreur(e?.message || 'Erreur lors de la suppression du contrat');
        } finally {
            setContratSupprBusy(false);
        }
    };

    const creerContrat = async () => {
        if ((!dossier?.devis?.formula_subscription_id && !dossier?.devis?.formula_id) || !templateId) return;
        setContratBusy(true); setContratErreur('');
        try {
            const res: any = await moduleApi.createSubscription({
                contact_id: dossier.crm_contact_id,
                formula_subscription_id: dossier.devis.formula_subscription_id || undefined,
                devis_id: dossier.devis.formula_subscription_id ? undefined : dossier.devis.id,
                template_id: templateId,
                engagement_months: engagementMonths,
                prix_ttc_mensuel: Number(prixMensuel) || 0,
                payment_mode: paymentMode,
            });
            if (res?.error) { setContratErreur(res.error); return; }
            setContratOuvert(false);
            charger();
        } catch (e: any) {
            setContratErreur(e?.message || 'Erreur création du contrat');
        } finally {
            setContratBusy(false);
        }
    };

    const transitionner = async (payload: any) => {
        if (!dossierId) return;
        setBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.transitionPrestationDossier(dossierId, payload);
            if (res?.error) { setErreur(res.error); return; }
            charger();
        } catch (e: any) {
            // Visites multiples : il reste du travail à réaliser. On le dit, et on laisse l'établissement confirmer si c'est voulu.
            if (payload?.to_stage === 'prestation_realisee' && !payload?.force && e?.status === 409 && /Il reste du travail/.test(e?.message || '')) {
                const confirme = await confirmAction({ title: 'Il reste du travail à réaliser', description: e.message, confirmLabel: 'Confirmer : la prestation est réalisée', destructive: false });
                if (confirme) { setBusy(false); await transitionner({ ...payload, force: true }); return; }
                setErreur('');
            } else {
                setErreur(e?.message || 'Erreur lors du changement d\'étape.');
            }
        } finally { setBusy(false); }
    };

    // Rendez-vous créé mais e-mail de confirmation (ou lien de paiement) non parti : on le dit, au lieu de
    // laisser croire que tout est parti.
    const [avertissementRdv, setAvertissementRdv] = React.useState('');

    // Devis envoyé et accepté hors plateforme (messagerie de l'établissement, simple « oui » du client) :
    // aucune signature n'est exigée. Le serveur fait foi (`accepte_hors_plateforme`).
    const devisAccepteHorsPlateforme = async (mode?: string) => {
        const ok = await confirmAction({
            title: 'Le client a accepté le devis ?',
            description: `${mode ? `Accord reçu ${mode}. ` : ''}Le dossier passera directement à « Devis signé », sans signature ni envoi depuis la plateforme, et vous pourrez fixer le rendez-vous. La date, l'auteur et la façon dont le client a accepté sont conservés dans l'historique.`,
            confirmLabel: 'Oui, le devis est accepté',
            destructive: false,
        });
        if (ok) await transitionner({ to_stage: 'devis_signe', accepte_hors_plateforme: true, ...(mode ? { accord_mode: mode } : {}) });
    };

    // Renvoi du même devis (même PDF, même lien) au client qui ne retrouve plus le mail (2026-09-21, Emeni).
    const [renvoiBusy, setRenvoiBusy] = React.useState(false);
    const [renvoiInfo, setRenvoiInfo] = React.useState('');
    // « Renvoyer » et « Relancer » ne sont pas la même chose (Emeni, 2026-09-21) : renvoyer = le même mail à
    // l'identique pour un client qui ne l'a pas reçu ; relancer = un mail de relance distinct, configurable.
    const renvoyerDevis = async () => {
        if (!dossierId || renvoiBusy) return;
        const ok = await confirmAction({
            title: 'Renvoyer le devis',
            description: `Le client recevra à nouveau exactement le même mail, avec le devis en PDF et le même lien de signature, à ${dossier?.devis_sent_to || dossier?.email || 'son adresse'}.`,
            confirmLabel: 'Renvoyer',
            destructive: false,
        });
        if (!ok) return;
        setRenvoiBusy(true); setErreur(''); setRenvoiInfo('');
        try {
            const res: any = await moduleApi.resendPrestationDevis(dossierId);
            if (res?.error) { setErreur(res.error); return; }
            setRenvoiInfo(`Devis renvoyé à ${res?.recipient || 'l’adresse du client'}.`);
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du renvoi du devis.');
        } finally { setRenvoiBusy(false); }
    };
    const [relanceBusy, setRelanceBusy] = React.useState(false);
    // Lien de réservation (lot B, 2026-09-22) : le client choisit lui-même son créneau, le rendez-vous
    // arrive « en demande » dans le Planning, le dossier passe tout seul à « Rendez-vous réservé ».
    // Refuser une demande encore à étudier, en disant pourquoi (2026-09-24, Emeni). Différent de
    // « Marquer perdu / annulé » : ici le client peut être informé, avec le motif écrit par l'établissement.
    const [refusOuvert, setRefusOuvert] = React.useState(false);
    const [refusMotif, setRefusMotif] = React.useState('');
    const [refusPrevenir, setRefusPrevenir] = React.useState(true);
    const [refusBusy, setRefusBusy] = React.useState(false);
    const refuserDemande = async () => {
        if (!dossierId || refusBusy || refusMotif.trim().length < 3) return;
        setRefusBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.refuserDemandePrestation(dossierId, { motif: refusMotif.trim(), prevenir_client: refusPrevenir });
            if (res?.error) { setErreur(res.error); return; }
            setRefusOuvert(false); setRefusMotif('');
            charger();
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du refus de la demande.');
        } finally { setRefusBusy(false); }
    };
    const [lienResaBusy, setLienResaBusy] = React.useState(false);
    const [lienResaInfo, setLienResaInfo] = React.useState('');
    const [qrBusy, setQrBusy] = React.useState(false);
    // QR code imprimable (2026-09-26, demande d'Emeni) : un client sur formule le scanne pour fixer
    // lui-même chacun de ses passages restants, toute l'année — même lien permanent que « Envoyer le lien
    // de réservation », juste imprimé au lieu d'être envoyé par email.
    const telechargerQrReservation = async () => {
        if (!dossierId || qrBusy) return;
        setQrBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.getPrestationBookingLink(dossierId);
            if (res?.error || !res?.url) { setErreur(res?.error || "Le lien de réservation n'a pas pu être préparé."); return; }
            const { generateBookingQrPdf } = await import('./bookingQrPdfGenerator');
            await generateBookingQrPdf({ clientLabel: dossier?.label || '', companyName: companyName || '', url: res.url });
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la génération du QR code.');
        } finally { setQrBusy(false); }
    };
    // Espace client : même jeton permanent que la réservation, autre page (prochains passages, paiement choisi).
    const copierEspaceClient = async () => {
        if (!dossierId || lienResaBusy) return;
        setLienResaBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.getPrestationBookingLink(dossierId);
            if (res?.error || !res?.url) { setErreur(res?.error || "Le lien n'a pas pu être préparé."); return; }
            const url = String(res.url).replace('/prestation-reservation/', '/espace-client/');
            try { await navigator.clipboard.writeText(url); setLienResaInfo("Lien de l'espace client copié : collez-le dans un message au client."); }
            catch { setLienResaInfo(url); }
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de la préparation du lien.");
        } finally { setLienResaBusy(false); }
    };
    // Nouveau lien d'espace client : l'ancien (et son QR code imprimé) cesse de fonctionner. Confirmation avant d'agir.
    const renouvelerEspaceClient = async () => {
        if (!dossierId || lienResaBusy) return;
        if (!window.confirm("Créer un nouveau lien ? L'ancien lien et les QR codes déjà envoyés ne fonctionneront plus.")) return;
        setLienResaBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.renouvelerEspaceClient(dossierId);
            if (res?.error || !res?.url) { setErreur(res?.error || "Le lien n'a pas pu être renouvelé."); return; }
            try { await navigator.clipboard.writeText(res.url); setLienResaInfo("Nouveau lien copié. L'ancien ne fonctionne plus."); }
            catch { setLienResaInfo(res.url); }
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du renouvellement du lien.');
        } finally { setLienResaBusy(false); }
    };
    // QR code de l'ESPACE CLIENT (2026-10-19, demande d'Emeni) : le client le scanne pour voir ses passages, ses
    // photos avant/après et planifier la suite. Même lien permanent que la réservation, autre page.
    const telechargerQrEspaceClient = async () => {
        if (!dossierId || qrBusy) return;
        setQrBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.getPrestationBookingLink(dossierId);
            if (res?.error || !res?.url) { setErreur(res?.error || "Le lien n'a pas pu être préparé."); return; }
            const { generateBookingQrPdf } = await import('./bookingQrPdfGenerator');
            await generateBookingQrPdf({
                clientLabel: dossier?.label || '', companyName: companyName || '', url: String(res.url).replace('/prestation-reservation/', '/espace-client/'),
                titre: 'Votre espace client', consigne: 'Scannez ce code avec votre téléphone pour suivre vos prestations, voir vos photos avant/après et planifier votre prochain passage.', fichier: 'QR_espace_client',
            });
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la génération du QR code.');
        } finally { setQrBusy(false); }
    };
    // Adresse d'intervention : fixe (jamais ressaisie par le client) ou variable (2026-10-19).
    const [adresseMode, setAdresseMode] = React.useState<'fixe' | 'variable'>('variable');
    React.useEffect(() => { setAdresseMode((dossier as any)?.intervention_adresse_mode === 'fixe' ? 'fixe' : 'variable'); }, [(dossier as any)?.intervention_adresse_mode]);
    const changerAdresseMode = async (mode: 'fixe' | 'variable') => {
        if (!dossierId) return;
        setErreur('');
        let adresse: { address?: string } = {};
        if (mode === 'fixe' && !(dossier as any)?.crm_address && !(dossier as any)?.demande_adresse) {
            const saisie = window.prompt("Quelle est l'adresse d'intervention ? Elle sera utilisée à chaque visite, le client n'aura plus à la saisir.");
            if (!saisie || !saisie.trim()) return;
            adresse = { address: saisie.trim() };
        }
        try {
            const res: any = await moduleApi.setPrestationAdresseIntervention(dossierId, { mode, ...adresse });
            if (res?.error) { setErreur(res.error); return; }
            setAdresseMode(mode);
            setLienResaInfo(mode === 'fixe' ? "Adresse fixe enregistrée : le client ne la ressaisira plus." : "Le client pourra renseigner l'adresse à chaque visite.");
        } catch (e: any) { setErreur(e?.message || "L'enregistrement a échoué."); }
    };
    const envoyerLienReservation = async () => {
        if (!dossierId || lienResaBusy) return;
        const deja = !!dossier?.booking_link_sent_at;
        const ok = await confirmAction({
            title: deja ? 'Renvoyer le lien de réservation' : 'Envoyer le lien de réservation',
            description: `Le client recevra un email (modifiable dans Dossiers › Réglages › E-mails du devis) avec un lien pour choisir lui-même la date et l'heure du rendez-vous, à ${dossier?.email || 'son adresse'}. Le rendez-vous arrivera dans votre Planning, à confirmer en assignant un salarié.`,
            confirmLabel: deja ? 'Renvoyer' : 'Envoyer',
            destructive: false,
        });
        if (!ok) return;
        setLienResaBusy(true); setErreur(''); setLienResaInfo('');
        try {
            const res: any = await moduleApi.sendPrestationBookingLink(dossierId);
            if (res?.error) { setErreur(res.error); return; }
            setLienResaInfo(`Lien de réservation envoyé à ${res?.recipient || 'l’adresse du client'}.`);
            charger();
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'envoi du lien de réservation.");
        } finally { setLienResaBusy(false); }
    };
    const relancerClient = async () => {
        if (!dossierId || relanceBusy) return;
        const ok = await confirmAction({
            title: 'Relancer le client',
            description: `Le client recevra le mail de relance (modifiable dans Dossiers › Réglages › E-mails du devis), avec le même lien de signature, à ${dossier?.devis_sent_to || dossier?.email || 'son adresse'}.`,
            confirmLabel: 'Relancer',
            destructive: false,
        });
        if (!ok) return;
        setRelanceBusy(true); setErreur(''); setRenvoiInfo('');
        try {
            const res: any = await moduleApi.relancePrestationDevis(dossierId);
            if (res?.error) { setErreur(res.error); return; }
            setRenvoiInfo(`Relance envoyée à ${res?.recipient || 'l’adresse du client'}.`);
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la relance.');
        } finally { setRelanceBusy(false); }
    };

    // Dépôt manuel du devis signé quand le client l'a renvoyé par email au lieu de passer par le lien
    // public — évite de rester bloqué à « En attente du dépôt » alors que le document est déjà en main.
    const [depotManuelBusy, setDepotManuelBusy] = React.useState(false);
    const [signatureSurPlaceOuverte, setSignatureSurPlaceOuverte] = React.useState(false);
    const depotManuelRef = React.useRef<HTMLInputElement>(null);
    const deposerDevisSigneManuel = async (file: File) => {
        if (!dossierId || depotManuelBusy) return;
        setDepotManuelBusy(true); setErreur('');
        try {
            const document_base64: string = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(file);
            });
            const res: any = await moduleApi.deposerDevisSigneManuel(dossierId, { document_base64, file_name: file.name, mime_type: file.type || 'application/pdf' });
            if (res?.error) { setErreur(res.error); return; }
            charger();
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du dépôt du devis signé.');
        } finally { setDepotManuelBusy(false); }
    };

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={26} className="animate-spin text-content-muted" /></div>;
    if (!dossier) return <div className="p-6 text-center text-content-muted">{erreur || 'Dossier introuvable.'}</div>;

    const stage: PipelineStagePrestation = dossier.stage;
    const perdu = isTerminalStagePrestation(stage);
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
    // 2026-09-06 (Emeni) : « ça ne prend pas toute la place disponible sur la page depuis un PC ». Le
    // dossier porte désormais des aperçus de documents et l'historique des passages — la largeur de lecture
    // étroite d'origine (max-w-4xl) rendait le tout minuscule sur un grand écran.
    const largeurPage = 'max-w-[1600px]';

    // ─── Contrat d'abonnement (refonte 2026-10-14) ───────────────────────────────────────────────────
    // Quatre étapes lisibles d'un coup d'œil, UNE action principale par étape ; aperçu, dépôt manuel et
    // suppression passent dans « Plus ». Placé AVANT la réservation du rendez-vous : c'est son prérequis.
    const sub = dossier.subscription;
    const contratEtape = !sub ? 0
        : sub.status === 'actif' ? 4
        : sub.signed_document_storage_key ? 3
        : (sub.sent_at || sub.status === 'envoye' || sub.status === 'consulte') ? 2 : 1;
    const ETAPES_CONTRAT = ['Créer', 'Envoyer', 'Signature du client', 'Activer'];
    const totalAnnuelDevis = Number(dossier.devis?.total_ttc) || 0;
    const contratPanel = (
        <div className="rounded-2xl border border-border bg-surface-soft/60 p-4 space-y-3.5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm font-bold text-content flex items-center gap-2"><Repeat size={16} /> Contrat d'abonnement</p>
                {contratEtape === 4 && <span className="text-[11px] font-bold text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle2 size={13} /> Actif</span>}
            </div>
            <ol className="flex items-center gap-1 flex-wrap" aria-label="Avancement du contrat">
                {ETAPES_CONTRAT.map((nom, i) => (
                    <li key={nom} className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${i < contratEtape ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400' : i === contratEtape ? 'bg-brand-600 text-brand-fg' : 'bg-surface text-content-muted border border-border'}`}
                        aria-current={i === contratEtape ? 'step' : undefined}>
                        {i < contratEtape ? '✓ ' : `${i + 1}. `}{nom}
                    </li>
                ))}
            </ol>
            {contratErreur && <p className="text-xs text-red-500 font-semibold" role="alert">{contratErreur}</p>}
            {contratEtape >= 1 && contratEtape <= 2 && contratAlertes.length > 0 && (
                <ul className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 space-y-1" aria-label="Points à vérifier avant l'envoi">
                    {contratAlertes.map((t) => <li key={t} className="text-xs text-amber-700 dark:text-amber-400">⚠ {t}</li>)}
                </ul>
            )}

            {contratEtape === 0 && (
                templates.length === 0 ? (
                    <p className="text-xs text-content-muted italic">Aucun modèle de contrat : créez-en un depuis Contrats annuels › Réglages › Modèles.</p>
                ) : (
                    <div className="space-y-3">
                        <p className="text-xs text-content-muted">Le contrat reprend la formule et les prestations du devis signé. Vérifiez la durée et le règlement, puis créez-le.</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {templates.length > 1 && !monContratActif && (
                                <label className="space-y-1 sm:col-span-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-content-muted">Modèle</span>
                                    <select className={champ} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                                        <option value="">Choisir un modèle…</option>
                                        {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="space-y-1">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-content-muted">Durée d'engagement</span>
                                <div className="flex items-center gap-1.5">
                                    <input type="number" min={1} value={engagementMonths} onChange={(e) => setEngagementMonths(Math.max(1, Number(e.target.value) || 1))} className={champ} />
                                    <span className="text-xs text-content-muted shrink-0">mois</span>
                                </div>
                            </label>
                            <label className="space-y-1">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-content-muted">Règlement</span>
                                <select className={champ} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value as any)}>
                                    <option value="mensuel">Prélèvement mensuel (SEPA)</option>
                                    <option value="a_intervention">À chaque intervention</option>
                                </select>
                            </label>
                            {paymentMode === 'mensuel' && (
                                <label className="space-y-1 sm:col-span-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-content-muted">Mensualité TTC</span>
                                    <div className="flex items-center gap-1.5 max-w-xs">
                                        <input type="number" min={0} step="0.01" value={prixMensuel} className={champ}
                                            onChange={(e) => { prixMensuelModifie.current = true; setPrixMensuel(e.target.value); }} />
                                        <span className="text-xs text-content-muted shrink-0">€ / mois</span>
                                    </div>
                                </label>
                            )}
                        </div>
                        <p className="text-xs text-content-muted rounded-lg bg-surface border border-border px-3 py-2">
                            {paymentMode === 'mensuel'
                                ? (() => {
                                    const e = echeancier(totalAnnuelDevis, engagementMonths, Number(prixMensuel) || 0);
                                    return <>Le client paiera <b className="text-content">{e.ajustee ? `${e.nbMois - 1} × ${fmtMontant(e.mensualite)}` : `${fmtMontant(e.mensualite)} par mois pendant ${e.nbMois} mois`}</b>{e.ajustee ? <> puis <b className="text-content">{fmtMontant(e.derniere)}</b> en dernière mensualité</> : null}, soit <b className="text-content">{fmtMontant(e.total)}</b> au total{totalAnnuelDevis > 0 && Math.abs(e.total - totalAnnuelDevis) >= 0.005 ? <> (devis : {fmtMontant(totalAnnuelDevis)})</> : null}.</>;
                                })()
                                : <>Chaque intervention sera facturée au moment du passage, sur {engagementMonths} mois d'engagement.</>}
                        </p>
                        <Button disabled={!templateId || contratBusy} onClick={creerContrat}>
                            {contratBusy ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />} Créer le contrat
                        </Button>
                    </div>
                )
            )}

            {contratEtape === 1 && (
                <div className="space-y-2.5">
                    <p className="text-xs text-content-muted">Le contrat est prêt. Relisez-le avant de l'envoyer au client pour signature.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button disabled={contratSendBusy} onClick={envoyerContratInline}>
                            {contratSendBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer le contrat
                        </Button>
                        <MenuPlus items={[
                            { cle: 'apercu', icone: <FileSearch size={14} />, texte: 'Voir un aperçu', disabled: contratPreviewBusy, action: apercuContratInline },
                            { cle: 'depot', icone: <Download size={14} />, texte: 'Déposer le contrat signé reçu par e-mail', disabled: contratDepotBusy, action: () => depotContratRef.current?.click() },
                            { cle: 'suppr', icone: <XCircle size={14} />, texte: 'Supprimer et recréer', danger: true, disabled: contratSupprBusy, action: supprimerContratBrouillon },
                        ]} />
                    </div>
                </div>
            )}

            {contratEtape === 2 && (
                <div className="space-y-2.5">
                    <p className="text-xs text-content-muted">Contrat envoyé{sub?.sent_at ? ` le ${fmtDate(sub.sent_at)}` : ''} — en attente de la signature du client.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="ghost" disabled={contratSendBusy} onClick={envoyerContratInline}>
                            {contratSendBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Renvoyer le contrat
                        </Button>
                        <MenuPlus items={[
                            { cle: 'apercu', icone: <FileSearch size={14} />, texte: 'Voir un aperçu', disabled: contratPreviewBusy, action: apercuContratInline },
                            { cle: 'depot', icone: <Download size={14} />, texte: 'Déposer le contrat signé reçu par e-mail', disabled: contratDepotBusy, action: () => depotContratRef.current?.click() },
                        ]} />
                    </div>
                </div>
            )}

            {contratEtape === 3 && (
                <div className="space-y-2.5">
                    <p className="text-xs font-bold text-content flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-500" /> Exemplaire signé reçu — à vérifier.</p>
                    <Button disabled={contratValiderBusy} onClick={validerContratInline}>
                        {contratValiderBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Valider et activer l'abonnement
                    </Button>
                </div>
            )}

            {contratEtape === 4 && <p className="text-xs text-content-muted">Le contrat est actif : le premier rendez-vous peut être fixé ci-dessous.</p>}

            <input ref={depotContratRef} type="file" accept="application/pdf,image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) deposerContratSigneInline(f); e.target.value = ''; }} />
        </div>
    );

    return (
        <div className={`p-6 ${largeurPage} mx-auto space-y-6`}>
            <div className="flex items-center justify-between">
                <button onClick={() => navigate('/suivi-prestation')} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content">
                    <ArrowLeft size={15} /> Retour à la liste
                </button>
                {!perdu && stage !== 'cloturee' && (
                    <button onClick={() => setAbandonOuvert(true)} className="text-xs text-red-500 hover:text-red-400 font-bold flex items-center gap-1">
                        <XCircle size={13} /> Marquer perdu / annulé
                    </button>
                )}
            </div>

            <div>
                <h1 className="text-2xl font-black text-content">{dossier.label}</h1>
                <p className="text-sm text-content-muted mt-0.5">{dossier.email} {dossier.phone ? `· ${dossier.phone}` : ''}</p>
            </div>

            {/* Frise en quatre étapes + « Prochaine action » (refonte Dossiers clients) : agit par les mêmes fonctions que le panneau ci-dessous. */}
            <SuiviDossierResume dossier={dossier} dossierId={dossierId} busy={busy || relanceBusy}
                onRelancer={relancerClient} onAccepte={devisAccepteHorsPlateforme} />

            {/* Le recap ecrit dans notes_appel fait doublon avec le bloc « Demande recue depuis le
                simulateur » ci-dessous, qui dit la meme chose en mieux (prix relus, photos, message) —
                on ne l'affiche donc que pour les dossiers qui ne viennent pas du site. */}
            {dossier.notes_appel && dossier.source !== 'simulateur' && (
                <div className="rounded-2xl border border-border bg-surface p-4 space-y-2.5">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-brand-500/10 flex items-center justify-center text-brand-600 dark:text-brand-400 shrink-0">
                            <MessageSquareText size={16} />
                        </div>
                        <h3 className="font-bold text-content text-sm">Réponse à la Démarche</h3>
                    </div>
                    <div className="space-y-1.5 pl-10">
                        {dossier.notes_appel.split('\n').filter(Boolean).map((ligne: string, i: number) => (
                            <p key={i} className="text-sm text-content-muted">{ligne}</p>
                        ))}
                    </div>
                </div>
            )}

            {/* Frise des étapes — insère « Contrat envoyé »/« Contrat signé » entre Devis signé et Rendez-vous
                réservé quand le devis porte une formule (jamais stocké, dérivé du statut de l'abonnement). */}
            {/* Le détail fin des étapes (dont « Contrat envoyé / signé ») est replié en bas de page, avant l'historique. */}
            {perdu && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                    <p className="text-sm font-bold text-content">{PIPELINE_STAGE_LABELS_PRESTATION[stage]}</p>
                    {dossier.lost_reason && <p className="text-xs text-content-muted mt-1">{dossier.lost_reason}</p>}
                </div>
            )}

            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}

            {origine && <OrigineDemandeDevis origine={origine} />}

            {!perdu && <DemarchageDomicile dossier={dossier} />}
            {dossierId && <SignatureSurPlaceModal dossierId={dossierId} open={signatureSurPlaceOuverte} onClose={() => setSignatureSurPlaceOuverte(false)} onDone={charger} />}

            {/* Panneau d'action de l'étape courante */}
            {!perdu && (
                <div id="panneau-etape" className="bg-surface border border-border rounded-2xl p-5 space-y-4 scroll-mt-4">
                    {stage === 'demande_recue' && (
                        <div className="space-y-3">
                            <p className="text-sm font-bold text-content flex items-center gap-2"><FileSearch size={16} /> Demande à étudier</p>
                            <p className="text-xs text-content-muted">
                                Le client attend un devis. Regardez ses photos ci-dessus, puis ouvrez le devis : ses prestations y sont
                                reprises telles qu'il les a simulées — à vous d'ajuster les quantités, d'ajouter un traitement
                                supplémentaire si les taches le demandent, ou de corriger le tarif.
                            </p>
                            <Button
                                disabled={busy}
                                onClick={async () => {
                                    // Les lignes simulées deviennent le point de départ du devis : personne ne
                                    // ressaisit ce que le client a déjà choisi.
                                    if (origine?.estimation) {
                                        setItemsDemande(origine.estimation.lignes.map((l) => ({
                                            catalog_item_id: l.id,
                                            quantity: l.qty,
                                            manual_discount_percent: 0,
                                            offered_quantity: 0,
                                        })));
                                    }
                                    await transitionner({ to_stage: 'dossier_cree' });
                                    // composeurOuvert reste FALSE (2026-09-26, bug signale par Emeni) : le forcer a
                                    // true ici sautait systematiquement FormulaPassagesPlanner, meme pour un pro
                                    // avec une formule a plusieurs passages — le devis partait alors sur un seul
                                    // passage, sans passage_number, et le lien de reservation/QR n'avait ensuite
                                    // plus aucun passage a proposer au client.
                                }}
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSearch size={14} />} Étudier et établir le devis
                            </Button>
                        </div>
                    )}

                    {stage === 'dossier_cree' && (
                        composeurOuvert && dossier.source === 'simulateur' && !origine ? (
                            // Le composeur ne lit ses lignes de départ qu'à son ouverture : on attend la demande.
                            <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" /></div>
                        ) : composeurOuvert ? (
                            <DevisPrestationComposer
                                companyName={companyName}
                                logoUrl={logoUrl}
                                dossierId={dossierId}
                                initialContactId={dossier.crm_contact_id || null}
                                initialFormulaSubscriptionId={planPasses ? planPasses.formulaSubscriptionId : (dossier.selected_formula_id || null)}
                                initialSelectedOptionalCatalogItemIds={(() => { try { return JSON.parse(dossier.selected_formula_optional_item_ids || '[]'); } catch { return []; } })()}
                                initialItems={(() => {
                                    // Sans ce repli, une demande déjà passée à « dossier créé » (page rechargée, ou devis
                                    // réinitialisé) ouvrait un composeur vide : itemsDemande ne vit qu'en mémoire.
                                    const base = planPasses ? planPasses.items : (itemsDemande ?? origine?.estimation?.lignes.map((l) => ({
                                        catalog_item_id: l.id, quantity: l.qty, manual_discount_percent: 0, offered_quantity: 0,
                                    })));
                                    // Les frais kilométriques vus par le client au simulateur deviennent une ligne
                                    // libre du devis (signalé par Emeni, 2026-10-05 : ils n'apparaissaient pas).
                                    const km = origine?.km_fee;
                                    if (!base || !km || (!km.waived && !(km.amount > 0))) return base;
                                    return [...base, {
                                        catalog_item_id: '', quantity: 1, manual_discount_percent: 0, offered_quantity: 0,
                                        label: `Frais kilométriques (${km.distance_km} km ${km.aller_retour ? 'aller-retour' : 'aller'})${km.waived ? ' — Offerts' : ''}`,
                                        unit_price_ttc: km.waived ? 0 : km.amount, tax_rate: 20,
                                    }];
                                })()}
                                editingDevisId={editionDevisId}
                                fraisKmActuels={origine?.km_fee && (origine.km_fee.waived || origine.km_fee.amount > 0) ? {
                                    label: `Frais kilométriques (${origine.km_fee.distance_km} km ${origine.km_fee.aller_retour ? 'aller-retour' : 'aller'})${origine.km_fee.waived ? ' — Offerts' : ''}`,
                                    unit_price_ttc: origine.km_fee.waived ? 0 : origine.km_fee.amount,
                                } : null}
                                onSent={() => { setComposeurOuvert(false); setPlanPasses(null); setEditionDevisId(null); charger(); }}
                                onHistory={(id) => setHistoriqueDevisId(id)}
                                onReset={async () => {
                                    // Un brouillon déjà composé serait sinon ré-ouvert instantanément par la
                                    // reprise automatique ci-dessus (dossier.devis reste en brouillon) —
                                    // « Réinitialiser » doit vraiment repartir de zéro, donc on le supprime.
                                    if (editionDevisId) { try { await moduleApi.deletePrestationDevis(editionDevisId); } catch {} }
                                    setComposeurOuvert(false); setPlanPasses(null); setEditionDevisId(null); charger();
                                }}
                            />
                        ) : (
                            <FormulaPassagesPlanner
                                dossierId={dossierId!}
                                contactId={dossier.crm_contact_id || null}
                                contactType={dossier.type || null}
                                selectedFormulaId={dossier.selected_formula_id || null}
                                onSkip={() => setComposeurOuvert(true)}
                                onDone={({ items, formulaSubscriptionId }) => { setPlanPasses({ items, formulaSubscriptionId }); setComposeurOuvert(true); }}
                            />
                        )
                    )}

                    <PaiementChoisi dossier={dossier} />

                    {avertissementRdv && (
                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-content-muted flex items-start justify-between gap-3">
                            <span><strong className="text-content">Rendez-vous créé, mais l'e-mail n'est pas parti.</strong> {avertissementRdv}</span>
                            <button type="button" onClick={() => setAvertissementRdv('')} className="shrink-0 font-bold text-content-muted hover:text-content">Fermer</button>
                        </div>
                    )}


                    {stage === 'devis_envoye' && (
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs text-content-muted mt-1">
                                    {dossier.devis ? <>Devis de <span className="font-bold text-content">{fmtMontant(dossier.devis.total_ttc)}</span> envoyé avec un lien pour le signer en ligne.</> : 'Le devis a été envoyé avec un lien pour le signer en ligne.'}
                                </p>
                            </div>
                            {dossier.devis_signed_received_at ? (
                                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 space-y-2">
                                    <p className="text-sm font-bold text-content flex items-center gap-1.5"><CheckCircle2 size={15} className="text-green-500" />
                                        {dossier.signature ? 'Devis signé en ligne par le client, à vérifier' : 'Devis signé déposé par le client, à vérifier'}
                                    </p>
                                    <p className="text-xs text-content-muted">Reçu le {fmtDate(dossier.devis_signed_received_at)} — consultable dans « Documents du dossier » ci-dessous.</p>
                                    {dossier.signature && <SignatureEnLigneResume signature={dossier.signature} />}
                                    <Button disabled={busy} onClick={() => transitionner({ to_stage: 'devis_signe' })}>
                                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Valider le devis signé
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* Chez le client : il signe au doigt sur cet écran (démarchage à domicile). */}
                                    <Button onClick={() => setSignatureSurPlaceOuverte(true)}>
                                        <PenLine size={14} /> Faire signer sur place
                                    </Button>
                                    {/* Une action principale ; renvoyer, historique et dépôt manuel passent dans « Plus ». */}
                                    <MenuPlus items={[
                                        { cle: 'renvoyer', icone: <Send size={14} />, texte: 'Renvoyer le devis à l\'identique', disabled: renvoiBusy || relanceBusy, action: renvoyerDevis },
                                        ...(dossier.devis?.id ? [{ cle: 'hist', icone: <History size={14} />, texte: 'Historique des envois', action: () => setHistoriqueDevisId(dossier.devis.id) }] : []),
                                        { cle: 'depot', icone: <Download size={14} />, texte: depotManuelBusy ? 'Dépôt...' : 'Déposer le devis signé reçu par e-mail', disabled: depotManuelBusy, action: () => depotManuelRef.current?.click() },
                                    ]} />
                                    <input ref={depotManuelRef} type="file" accept="application/pdf,image/*" className="hidden"
                                        onChange={(e) => { const f = e.target.files?.[0]; if (f) deposerDevisSigneManuel(f); e.target.value = ''; }} />
                                </div>
                            )}
                            {renvoiInfo && <p className="text-xs text-green-600 dark:text-green-400 font-semibold">{renvoiInfo}</p>}
                        </div>
                    )}

                    {stage === 'devis_signe' && (() => {
                        // Client avec formule : l'abonnement doit être ACTIF (contrat signé et validé par
                        // l'établissement) avant de pouvoir fixer le premier rendez-vous d'intervention —
                        // demande d'Emeni (2026-09-03) : « c'est qu'une fois signée que son abonnement prend
                        // effet, et à partir de là on peut fixer le premier RDV ». Sans formule (particulier,
                        // ou pro sans engagement), aucun contrat n'est jamais requis : le RDV reste réservable
                        // dès le devis signé, comportement inchangé.
                        // Devis chiffré avec une formule mais sans souscription (dossier ouvert par téléphone) : le
                        // contrat reste possible tant que le module est disponible — il crée la souscription.
                        const contratRequis = !!dossier.devis?.formula_subscription_id || (!!dossier.devis?.formula_id && contratDispo);
                        const attendContrat = contratRequis && dossier.subscription?.status !== 'actif';
                        return (
                        <div className="space-y-4">
                            {contratRequis && contratPanel}
                            {attendContrat ? (
                                <p className="text-xs text-content-muted flex items-center gap-1.5"><CalendarClock size={13} /> Le rendez-vous se réserve une fois le contrat activé.</p>
                            ) : (
                            <div className="space-y-3">
                            <p className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={16} /> Réserver le rendez-vous</p>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button disabled={lienResaBusy} onClick={envoyerLienReservation}>
                                    {lienResaBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {dossier.booking_link_sent_at ? 'Renvoyer le lien de réservation' : 'Envoyer le lien de réservation au client'}
                                </Button>
                                <MenuPlus label="Partager" items={[
                                    { cle: 'espace', icone: <Link2 size={14} />, texte: "Copier le lien de l'espace client", disabled: lienResaBusy, action: copierEspaceClient },
                                    { cle: 'qr-espace', icone: <QrCode size={14} />, texte: "QR code de l'espace client", disabled: qrBusy, action: telechargerQrEspaceClient },
                                    { cle: 'renouveler-espace', icone: <Link2 size={14} />, texte: "Renouveler le lien (l'ancien s'arrête)", disabled: lienResaBusy, action: renouvelerEspaceClient },
                                    ...(dossier.subscription ? [{ cle: 'qr-resa', icone: <QrCode size={14} />, texte: 'QR code de réservation', disabled: qrBusy, action: telechargerQrReservation }] : []),
                                    { cle: 'adresse-variable', icone: <MapPin size={14} />, texte: (adresseMode === 'variable' ? '✓ ' : '') + "Adresse : le client la renseigne", action: () => changerAdresseMode('variable') },
                                    { cle: 'adresse-fixe', icone: <MapPin size={14} />, texte: (adresseMode === 'fixe' ? '✓ ' : '') + 'Adresse : fixe, jamais ressaisie', action: () => changerAdresseMode('fixe') },
                                ]} />
                                {dossier.booking_link_sent_at && <span className="text-[11px] text-content-muted">Envoyé le {fmtDate(dossier.booking_link_sent_at)}</span>}
                            </div>
                            <p className="text-xs text-content-muted">Le client choisit lui-même son créneau sur le lien. Adresse d'intervention : {adresseMode === 'fixe' ? 'fixe, il ne la ressaisit pas.' : 'il la renseigne à chaque visite.'}</p>
                            {lienResaInfo && <p className="text-xs text-green-600 dark:text-green-400 font-semibold">{lienResaInfo}</p>}
                            <details className="group rounded-xl border border-border">
                                <summary className="cursor-pointer select-none px-3.5 py-2.5 text-sm font-bold text-content flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                                    <span className="flex items-center gap-2"><CalendarClock size={15} /> Planifier moi-même</span>
                                    <ChevronDown size={15} className="text-content-muted transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="px-3.5 pb-3.5 space-y-3">
                            {/* On planifie directement depuis ici avec les prestations déjà convenues au devis
                                (2026-09-06, Emeni — avant, il fallait quitter le dossier et tout ressaisir dans
                                le Planning) : passage par passage avec une formule, en un seul rendez-vous sans
                                (2026-09-21, Emeni : le bloc n'apparaissait qu'avec une formule, un particulier
                                venu du simulateur n'avait que le rattachement manuel). Le rattachement d'un
                                rendez-vous existant reste disponible juste en dessous, en secours. */}
                            {dossier.devis && (
                                <div className="pb-3 mb-3 border-b border-border">
                                    <PassagesTracker
                                        devisItems={dossier.devis?.items || []}
                                        formulaAppointments={dossier.formula_appointments || []}
                                        formulaInfo={dossier.formula_info || null}
                                        crmContactId={dossier.crm_contact_id}
                                        formulaSubscriptionId={dossier.devis.formula_subscription_id || null}
                                        dossierId={dossierId}
                                        onBooked={(appointmentId, avertissement) => {
                                            setAvertissementRdv(avertissement || '');
                                            if (dossier.stage === 'devis_signe') transitionner({ to_stage: 'rdv_reserve', appointment_id: appointmentId });
                                            else charger();
                                        }}
                                    />
                                </div>
                            )}
                            <p className="text-xs text-content-muted">Ou rattachez un rendez-vous déjà créé dans le Planning (rendez-vous) — rien n'est recopié, le rendez-vous reste la seule source.</p>
                            {candidats.length > 0 ? (
                                <div className="space-y-2">
                                    <select className={champ} value={candidatChoisi} onChange={(e) => setCandidatChoisi(e.target.value)}>
                                        <option value="">Choisir un rendez-vous existant…</option>
                                        {candidats.map((a) => <option key={a.id} value={a.id}>{fmtDate(a.appointment_date)} {a.start_time} — {a.prestation_label} ({fmtMontant(a.total_amount_ttc)})</option>)}
                                    </select>
                                    <Button disabled={!candidatChoisi || busy} onClick={() => transitionner({ to_stage: 'rdv_reserve', appointment_id: candidatChoisi })}>
                                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Rattacher ce rendez-vous
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-xs text-content-muted italic">Aucun rendez-vous disponible pour ce contact. Créez-en un dans « Planning (rendez-vous) », puis revenez ici.</p>
                            )}
                                </div>
                            </details>
                            </div>
                            )}
                        </div>
                        );
                    })()}

                    {(dossier.visites || []).length > 0 && <VisitesMission visites={dossier.visites} totalDevis={dossier.formula_info ? 0 : Number(dossier.devis?.total_ttc) || 0} />}

                    {stage === 'rdv_reserve' && (
                        <div className="space-y-3">
                            <p className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={16} /> Rendez-vous à réaliser</p>
                            {dossier.appointment ? (
                                <p className="text-xs text-content-muted">{fmtDate(dossier.appointment.appointment_date)} {dossier.appointment.start_time} — statut : {dossier.appointment.status}</p>
                            ) : null}
                            <Button disabled={busy || dossier.appointment?.status !== 'termine'} onClick={() => transitionner({ to_stage: 'prestation_realisee' })}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Marquer la prestation réalisée
                            </Button>
                            {dossier.appointment?.status !== 'termine' && <p className="text-[11px] text-content-muted italic">Marquez d'abord le rendez-vous « Terminé » dans le Planning.</p>}
                        </div>
                    )}

                    {stage === 'prestation_realisee' && (
                        <div className="space-y-3">
                            <p className="text-sm font-bold text-content flex items-center gap-2"><CheckCircle2 size={16} /> Clôturer le dossier</p>
                            {dossier.appointment?.facture_id ? (
                                <p className="text-xs text-content-muted">Une facture est liée à ce rendez-vous.</p>
                            ) : (
                                <p className="text-xs text-amber-500">Aucune facture liée à ce rendez-vous pour le moment.</p>
                            )}
                            <Button disabled={busy} onClick={() => transitionner({ to_stage: 'cloturee', force: !dossier.appointment?.facture_id })}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Clôturer
                            </Button>
                        </div>
                    )}

                    {stage === 'cloturee' && (
                        <p className="text-sm font-bold text-green-500 flex items-center gap-2"><CheckCircle2 size={16} /> Dossier clôturé le {fmtDate(dossier.closed_at)}</p>
                    )}
                </div>
            )}

            {/* Refuser une demande TANT QUE RIEN N'EST PARTI chez le client — donc à « Demande reçue » comme
                à « Dossier créé », où le devis est composé mais pas envoyé (2026-09-24, Emeni). Une fois le
                devis envoyé, c'est « Marquer perdu / annulé » qui s'applique : le client attend une réponse. */}
            {(stage === 'demande_recue' || stage === 'dossier_cree') && (
                refusOuvert ? (
                    <div className="bg-surface border border-red-500/30 rounded-2xl p-5 space-y-2.5">
                        <p className="text-sm font-bold text-content">Pourquoi refusez-vous cette demande ?</p>
                        <textarea rows={2} className={`${champ} resize-none`} value={refusMotif} onChange={(e) => setRefusMotif(e.target.value)}
                            placeholder="Hors de notre zone d'intervention, montant trop faible pour un déplacement…" />
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={refusPrevenir} onChange={(e) => setRefusPrevenir(e.target.checked)} className="accent-brand-600 mt-0.5" />
                            <span className="text-[11px] text-content-muted">
                                <b className="text-content">Prévenir le client</b> — il reçoit un email avec ce motif, tel que vous l'écrivez.
                                Le texte de l'email se règle dans Paramètres &gt; Notifications.
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => { setRefusOuvert(false); setRefusMotif(''); }}>Annuler</Button>
                            <Button variant="danger" disabled={refusMotif.trim().length < 3 || refusBusy} onClick={refuserDemande}>
                                {refusBusy ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />} Refuser la demande
                            </Button>
                        </div>
                    </div>
                ) : (
                    <button type="button" onClick={() => setRefusOuvert(true)} disabled={busy}
                        className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-red-500 transition-colors">
                        <XCircle size={13} /> Refuser cette demande
                    </button>
                )
            )}

            {/* Toujours visible, quelle que soit l'étape : le dossier garde une trace consultable de ce qui a
                été produit aux étapes précédentes. */}
            <DocumentsDuDossier
                dossierId={dossierId!}
                devisToken={dossier.devis_public_token}
                devisDocument={dossier.devis_document}
                devisSigneDocument={dossier.devis_signe_document}
                subscriptionId={dossier.subscription?.id}
                subscriptionSignedDocument={dossier.subscription?.signed_document_storage_key ? { file_name: dossier.subscription.signed_document_file_name } : null}
                ApercuDevisSigne={DevisSigneApercuPrestation}
                ApercuContrat={ContratApercuInline}
                ApercuContratSigne={ContratSigneApercuPrestation}
            />

            {abandonOuvert && (
                <div className="bg-surface border border-red-500/30 rounded-2xl p-5 space-y-3">
                    <p className="text-sm font-bold text-content">Pourquoi ce dossier n'aboutit pas ?</p>
                    <textarea rows={2} className={`${champ} resize-none`} value={motifAbandon} onChange={(e) => setMotifAbandon(e.target.value)} placeholder="Budget, date indisponible, autre prestataire…" />
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => setAbandonOuvert(false)}>Annuler</Button>
                        <Button variant="danger" disabled={!motifAbandon.trim() || busy} onClick={async () => {
                            const abandon = ['devis_signe', 'rdv_reserve', 'prestation_realisee'].includes(stage) ? 'annulee' : 'perdue';
                            await transitionner({ to_stage: abandon, lost_reason: motifAbandon });
                            setAbandonOuvert(false);
                        }}>Confirmer</Button>
                    </div>
                </div>
            )}

            {!perdu && stage !== 'dossier_cree' && stage !== 'demande_recue' && (
                <button disabled={busy} onClick={() => transitionner({ back: true })} className="flex items-center gap-1.5 text-xs text-content-muted hover:text-content font-bold">
                    <Undo2 size={13} /> Revenir à l'étape précédente
                </button>
            )}

            {!perdu && (
                    <details className="group">
                        <summary className="cursor-pointer text-[11px] font-bold text-content-muted hover:text-content select-none">Voir le détail des étapes</summary>
                        <div className="flex items-center gap-1 overflow-x-auto pb-1 pt-2">
                            {buildDisplayStagesPrestation(dossier).map((s, i) => (
                                <React.Fragment key={s.key}>
                                    {i > 0 && <div className={`h-0.5 w-6 shrink-0 ${s.done || s.current ? 'bg-brand-500' : 'bg-border'}`} />}
                                    <div className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap ${s.current ? 'bg-brand-600 text-brand-fg' : s.done ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'bg-surface-soft text-content-muted'}`}>
                                        {s.label}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </details>
            )}

            {dossier.journal?.length > 0 && (
                <details className="pt-4 border-t border-border group">
                    <summary className="cursor-pointer select-none text-xs font-black uppercase tracking-widest text-content-muted mb-2 flex items-center gap-1.5 [&::-webkit-details-marker]:hidden">
                        Historique du dossier <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="space-y-1.5">
                        {dossier.journal.map((j: any) => (
                            <div key={j.id} className="text-xs text-content-muted flex items-center gap-2">
                                <span className="text-content-muted/70">{fmtDate(j.changed_at)}</span>
                                <span>{PIPELINE_STAGE_LABELS_PRESTATION[j.to_stage as PipelineStagePrestation] || j.to_stage}</span>
                                {j.note && <span className="italic">— {j.note}</span>}
                            </div>
                        ))}
                    </div>
                </details>
            )}

            {historiqueDevisId && (
                <HistoriqueDevisModal devisId={historiqueDevisId} onClose={() => setHistoriqueDevisId(null)} />
            )}
        </div>
    );
}
